import indexDbService from "../../helpers/dbHelper"
import audioNotificationService from "../../services/audio-notification-service"

function getOrdersNotificationPlayCount(terminal) {
  const setting = terminal?.db_settings?.find(
    (s) => s.name === "orders_notification_play_count",
  );
  const parsed = parseInt(setting?.value, 10);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return parsed;
}

const state = {
  notifications: [],
  alertActive: false  // Track if notification alert is currently active
}

const getters = {
  allNotifications: (state) => state.notifications,
  unreadCount: (state) => state.notifications.filter(n => !n.read).length,
  unreadNotifications: (state) => state.notifications.filter(n => !n.read),
  isAlertActive: (state) => state.alertActive
}

const mutations = {
  SET_NOTIFICATIONS(state, notifications) {
    state.notifications = notifications
  },

  ADD_NOTIFICATION(state, notification) {
    // Add notification with unique ID and timestamp
    const newNotification = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    }
    state.notifications.unshift(newNotification)

    // Keep only last 50 notifications
    if (state.notifications.length > 50) {
      state.notifications = state.notifications.slice(0, 50)
    }

    // Save to IndexedDB
    indexDbService.saveToDb('notifications', state.notifications)
  },

  MARK_AS_READ(state, notificationId) {
    const notification = state.notifications.find(n => n.id === notificationId)
    if (notification) {
      notification.read = true
      // Save to IndexedDB
      indexDbService.saveToDb('notifications', state.notifications)
    }
  },

  MARK_ALL_AS_READ(state) {
    state.notifications.forEach(notification => {
      notification.read = true
    })
    // Save to IndexedDB
    indexDbService.saveToDb('notifications', state.notifications)
  },

  REMOVE_NOTIFICATION(state, notificationId) {
    state.notifications = state.notifications.filter(n => n.id !== notificationId)
    // Save to IndexedDB
    indexDbService.saveToDb('notifications', state.notifications)
  },

  CLEAR_ALL_NOTIFICATIONS(state) {
    state.notifications = []
    // Clear from IndexedDB
    indexDbService.clearAllData('notifications')
  },

  SET_ALERT_ACTIVE(state, isActive) {
    state.alertActive = isActive
  }
}

const actions = {
  async loadNotifications({ commit }) {
    try {
      const notifications = await indexDbService.getDbData('notifications')
      commit('SET_NOTIFICATIONS', notifications || [])
    } catch (error) {
      console.error('Error loading notifications from IndexedDB:', error)
      commit('SET_NOTIFICATIONS', [])
    }
  },

  addNotification({ commit, rootGetters }, notification) {
    // Skip order notifications entirely in Kiosk mode
    const isKioskMode = rootGetters["config/isKioskMode"];
    const onlineOrderCalls = [
      "fetchPendingOrder",
      "newOnlineOrder",
      "orderUpdate",
    ];
    const isOnlineOrder =
      notification.data && onlineOrderCalls.includes(notification.data.call);

    if (isKioskMode && isOnlineOrder) {
      return;
    }

    commit("ADD_NOTIFICATION", notification);

    // Start alert sound for Online Order notifications
    if (isOnlineOrder) {
      try {
        // Check terminal settings for notification sound
        const terminal = rootGetters["config/terminal"];
        const ordersNotificationSetting = terminal?.db_settings?.find(
          (s) => s.name === "orders_notifications",
        );
        const isNotificationEnabled = ordersNotificationSetting
          ? ordersNotificationSetting.value === "true"
          : true;

        if (isNotificationEnabled) {
          const playCount = getOrdersNotificationPlayCount(terminal);
          audioNotificationService.restart({
            playCount,
            onComplete: () => commit("SET_ALERT_ACTIVE", false),
          });
          commit("SET_ALERT_ACTIVE", true);
        }
      } catch (error) {
        console.error('Error starting notification alert:', error);
      }
    }
  },

  markAsRead({ commit }, notificationId) {
    commit('MARK_AS_READ', notificationId)
  },

  markAllAsRead({ commit }) {
    commit('MARK_ALL_AS_READ')
  },

  removeNotification({ commit }, notificationId) {
    commit('REMOVE_NOTIFICATION', notificationId)
  },

  clearAllNotifications({ commit }) {
    commit('CLEAR_ALL_NOTIFICATIONS')
  },

  dismissAlert({ commit }) {
    audioNotificationService.stop();
    commit('SET_ALERT_ACTIVE', false);
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
